import type { Ack, CommandName, CommandPayload, CommandResults } from '@crowdset/contracts';
import { createContext, useCallback, useContext, useState } from 'react';
import type { RoomConnection } from './connection';
import { newRequestId } from './connection';
import { useRoomStore } from './store';

export const RoomConnectionContext = createContext<RoomConnection | null>(null);

export function useConnection(): RoomConnection {
  const c = useContext(RoomConnectionContext);
  if (!c) throw new Error('useConnection outside a room');
  return c;
}

type WithoutRequestId<N extends CommandName> = Omit<CommandPayload<N>, 'requestId'>;

/**
 * Send a room command with a fresh requestId (idempotency key). Errors surface as toasts unless
 * `quiet` is set; the typed ack is returned either way.
 */
export function useCommand<N extends CommandName>(
  name: N,
  opts: { quiet?: boolean; success?: string } = {},
) {
  const conn = useConnection();
  const [pending, setPending] = useState(false);
  const run = useCallback(
    async (payload: WithoutRequestId<N>): Promise<Ack<CommandResults[N]>> => {
      setPending(true);
      try {
        const ack = await conn.command(name, {
          ...payload,
          requestId: newRequestId(),
        } as CommandPayload<N>);
        if (!ack.ok && !opts.quiet) useRoomStore.getState().toast(ack.error.message, 'error');
        if (ack.ok && opts.success) useRoomStore.getState().toast(opts.success, 'ok');
        return ack;
      } finally {
        setPending(false);
      }
    },
    [conn, name, opts.quiet, opts.success],
  );
  return { run, pending };
}
